// /app/frontend/src/components/admin/ReorderSuggestionsTab.jsx
// Automatic reorder suggestions UI with supplier grouping and PO creation

import React, { useState, useEffect } from 'react';
import { 
  Package, AlertTriangle, Truck, DollarSign, Check, X, ShoppingCart, 
  Send, ChevronDown, ChevronRight, Building2, Mail, Phone, Loader2,
  Plus, Minus, AlertCircle, CheckCircle2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ReorderSuggestionsTab = ({ token }) => {
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const [selectedItems, setSelectedItems] = useState({});
  const [quantities, setQuantities] = useState({});
  
  // PO Creation Modal
  const [showCreatePOModal, setShowCreatePOModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [creatingPO, setCreatingPO] = useState(false);
  const [poNotes, setPONotes] = useState('');
  
  // Email PO Modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailingPO, setEmailingPO] = useState(null);
  const [emailData, setEmailData] = useState({ to: '', subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/inventory/reorder-suggestions`, { headers });
      setSuggestions(res.data);
      
      // Initialize quantities with suggested values
      const newQuantities = {};
      const newSelected = {};
      res.data.suppliers?.forEach(supplier => {
        supplier.items?.forEach(item => {
          newQuantities[item.item_id] = item.suggested_quantity;
          newSelected[item.item_id] = true; // Select all by default
        });
      });
      setQuantities(newQuantities);
      setSelectedItems(newSelected);
      
      // Expand first supplier by default
      if (res.data.suppliers?.length > 0) {
        setExpandedSuppliers({ [res.data.suppliers[0].supplier_id || 'no_supplier']: true });
      }
    } catch (error) {
      toast.error('Failed to load reorder suggestions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSupplier = (supplierId) => {
    setExpandedSuppliers(prev => ({
      ...prev,
      [supplierId || 'no_supplier']: !prev[supplierId || 'no_supplier']
    }));
  };

  const toggleItem = (itemId) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const updateQuantity = (itemId, value) => {
    setQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(1, parseInt(value) || 1)
    }));
  };

  const getSelectedItemsForSupplier = (supplier) => {
    return supplier.items.filter(item => selectedItems[item.item_id]);
  };

  const getSupplierTotal = (supplier) => {
    return supplier.items
      .filter(item => selectedItems[item.item_id])
      .reduce((sum, item) => sum + (quantities[item.item_id] || item.suggested_quantity) * item.unit_cost, 0);
  };

  const openCreatePOModal = (supplier) => {
    setSelectedSupplier(supplier);
    setPONotes(`Auto-generated from reorder suggestions - ${new Date().toLocaleDateString()}`);
    setShowCreatePOModal(true);
  };

  const createPurchaseOrder = async () => {
    if (!selectedSupplier) return;
    
    const selectedItems_list = getSelectedItemsForSupplier(selectedSupplier);
    if (selectedItems_list.length === 0) {
      toast.error('Please select at least one item');
      return;
    }
    
    setCreatingPO(true);
    try {
      const res = await axios.post(`${API_URL}/admin/inventory/create-suggested-po`, {
        supplier_id: selectedSupplier.supplier_id,
        items: selectedItems_list.map(item => ({
          item_id: item.item_id,
          quantity: quantities[item.item_id] || item.suggested_quantity
        })),
        notes: poNotes
      }, { headers });
      
      toast.success(`Purchase Order ${res.data.po_number} created!`);
      setShowCreatePOModal(false);
      fetchSuggestions(); // Refresh
    } catch (error) {
      toast.error('Failed to create purchase order');
      console.error(error);
    } finally {
      setCreatingPO(false);
    }
  };

  const openEmailModal = (supplier) => {
    setEmailingPO(supplier);
    setEmailData({
      to: supplier.supplier_email || '',
      subject: `Purchase Order Request - ${new Date().toLocaleDateString()}`,
      message: `Dear ${supplier.supplier_name},\n\nPlease find below our purchase order request for the following items:\n\n${
        supplier.items
          .filter(item => selectedItems[item.item_id])
          .map(item => `• ${item.name} (${item.sku}) - Qty: ${quantities[item.item_id] || item.suggested_quantity} ${item.unit}`)
          .join('\n')
      }\n\nPlease confirm availability and expected delivery date.\n\nThank you.`
    });
    setShowEmailModal(true);
  };

  const sendEmailToSupplier = async () => {
    if (!emailData.to) {
      toast.error('Please enter supplier email');
      return;
    }
    
    setSendingEmail(true);
    try {
      // First create the PO
      const selectedItems_list = getSelectedItemsForSupplier(emailingPO);
      const poRes = await axios.post(`${API_URL}/admin/inventory/create-suggested-po`, {
        supplier_id: emailingPO.supplier_id,
        items: selectedItems_list.map(item => ({
          item_id: item.item_id,
          quantity: quantities[item.item_id] || item.suggested_quantity
        })),
        notes: `Emailed to supplier on ${new Date().toLocaleDateString()}`
      }, { headers });
      
      // Then send the email
      await axios.post(`${API_URL}/admin/purchase-orders/${poRes.data.id}/send-email`, {
        to_email: emailData.to,
        subject: emailData.subject,
        message: emailData.message
      }, { headers });
      
      toast.success(`PO ${poRes.data.po_number} created and emailed to supplier!`);
      setShowEmailModal(false);
      fetchSuggestions();
    } catch (error) {
      toast.error('Failed to send email');
      console.error(error);
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!suggestions || suggestions.total_suggestions === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
          <h3 className="text-xl font-semibold mb-2">All Stock Levels Healthy</h3>
          <p className="text-gray-500">No items currently need reordering.</p>
          <Button variant="outline" className="mt-4" onClick={fetchSuggestions}>
            Refresh
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-amber-600" />
            Reorder Suggestions
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {suggestions.total_suggestions} items need reordering across {suggestions.suppliers?.length || 0} supplier(s)
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-gray-500">Estimated Total</p>
            <p className="text-2xl font-bold text-amber-600">
              ${suggestions.total_estimated_cost?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <Button variant="outline" onClick={fetchSuggestions}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Supplier Groups */}
      <div className="space-y-4">
        {suggestions.suppliers?.map((supplier, idx) => {
          const supplierId = supplier.supplier_id || 'no_supplier';
          const isExpanded = expandedSuppliers[supplierId];
          const selectedCount = getSelectedItemsForSupplier(supplier).length;
          const supplierTotal = getSupplierTotal(supplier);
          
          return (
            <Card key={supplierId} className={supplier.critical_count > 0 ? 'border-red-200' : ''}>
              <CardHeader className="pb-3 cursor-pointer" onClick={() => toggleSupplier(supplier.supplier_id)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    <Building2 className="w-5 h-5 text-gray-400" />
                    <div>
                      <CardTitle className="text-lg">{supplier.supplier_name}</CardTitle>
                      <CardDescription className="flex items-center gap-4 mt-1">
                        {supplier.supplier_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {supplier.supplier_email}
                          </span>
                        )}
                        {supplier.supplier_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {supplier.supplier_phone}
                          </span>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {supplier.critical_count > 0 && (
                      <Badge variant="destructive">{supplier.critical_count} Critical</Badge>
                    )}
                    {supplier.warning_count > 0 && (
                      <Badge className="bg-amber-100 text-amber-700">{supplier.warning_count} Warning</Badge>
                    )}
                    <Badge variant="outline">{supplier.item_count} items</Badge>
                    <div className="text-right">
                      <p className="text-lg font-semibold">${supplierTotal.toFixed(2)}</p>
                      <p className="text-xs text-gray-500">{selectedCount} selected</p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              {isExpanded && (
                <CardContent className="pt-0">
                  {/* Items Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left w-10">
                            <Checkbox 
                              checked={selectedCount === supplier.items.length}
                              onCheckedChange={(checked) => {
                                supplier.items.forEach(item => {
                                  setSelectedItems(prev => ({ ...prev, [item.item_id]: !!checked }));
                                });
                              }}
                            />
                          </th>
                          <th className="px-4 py-3 text-left">Item</th>
                          <th className="px-4 py-3 text-center">Current</th>
                          <th className="px-4 py-3 text-center">Min Level</th>
                          <th className="px-4 py-3 text-center">Order Qty</th>
                          <th className="px-4 py-3 text-right">Unit Cost</th>
                          <th className="px-4 py-3 text-right">Line Total</th>
                          <th className="px-4 py-3 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplier.items.map((item) => {
                          const qty = quantities[item.item_id] || item.suggested_quantity;
                          const lineTotal = qty * item.unit_cost;
                          const isSelected = selectedItems[item.item_id];
                          
                          return (
                            <tr 
                              key={item.item_id} 
                              className={`border-t ${!isSelected ? 'opacity-50 bg-gray-50' : ''} ${
                                item.severity === 'critical' ? 'bg-red-50' : ''
                              }`}
                            >
                              <td className="px-4 py-3">
                                <Checkbox 
                                  checked={isSelected}
                                  onCheckedChange={() => toggleItem(item.item_id)}
                                />
                              </td>
                              <td className="px-4 py-3">
                                <div className="font-medium">{item.name}</div>
                                <div className="text-xs text-gray-500">{item.sku}</div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={item.current_quantity === 0 ? 'text-red-600 font-bold' : ''}>
                                  {item.current_quantity}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center text-gray-500">
                                {item.min_stock_level}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-1">
                                  <Button 
                                    variant="outline" 
                                    size="icon" 
                                    className="h-7 w-7"
                                    onClick={(e) => { e.stopPropagation(); updateQuantity(item.item_id, qty - 1); }}
                                  >
                                    <Minus className="w-3 h-3" />
                                  </Button>
                                  <Input
                                    type="number"
                                    value={qty}
                                    onChange={(e) => updateQuantity(item.item_id, e.target.value)}
                                    className="w-16 h-7 text-center"
                                    min="1"
                                  />
                                  <Button 
                                    variant="outline" 
                                    size="icon" 
                                    className="h-7 w-7"
                                    onClick={(e) => { e.stopPropagation(); updateQuantity(item.item_id, qty + 1); }}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                ${item.unit_cost.toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-right font-medium">
                                ${lineTotal.toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <Badge 
                                  variant={item.severity === 'critical' ? 'destructive' : 'outline'}
                                  className={item.severity === 'warning' ? 'bg-amber-100 text-amber-700' : ''}
                                >
                                  {item.current_quantity === 0 ? 'Out of Stock' : 
                                   item.severity === 'critical' ? 'Critical' : 
                                   item.severity === 'warning' ? 'Low' : 'Reorder'}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-500">
                      {selectedCount} of {supplier.items.length} items selected
                    </p>
                    <div className="flex items-center gap-2">
                      {supplier.supplier_email && (
                        <Button 
                          variant="outline"
                          onClick={() => openEmailModal(supplier)}
                          disabled={selectedCount === 0}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Email Supplier
                        </Button>
                      )}
                      <Button 
                        className="bg-amber-600 hover:bg-amber-700"
                        onClick={() => openCreatePOModal(supplier)}
                        disabled={selectedCount === 0}
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Create PO (${supplierTotal.toFixed(2)})
                      </Button>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Create PO Modal */}
      <Dialog open={showCreatePOModal} onOpenChange={setShowCreatePOModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Create Purchase Order
            </DialogTitle>
            <DialogDescription>
              Create a draft PO for {selectedSupplier?.supplier_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedSupplier && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{selectedSupplier.supplier_name}</p>
                    <p className="text-sm text-gray-500">
                      {getSelectedItemsForSupplier(selectedSupplier).length} items
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Total</p>
                    <p className="text-xl font-bold text-amber-600">
                      ${getSupplierTotal(selectedSupplier).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="max-h-48 overflow-y-auto border rounded-lg">
                {getSelectedItemsForSupplier(selectedSupplier).map(item => (
                  <div key={item.item_id} className="flex justify-between px-4 py-2 border-b last:border-0">
                    <span className="text-sm">{item.name}</span>
                    <span className="text-sm text-gray-500">
                      {quantities[item.item_id] || item.suggested_quantity} × ${item.unit_cost.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={poNotes}
                  onChange={(e) => setPONotes(e.target.value)}
                  placeholder="Add notes to this PO..."
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePOModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={createPurchaseOrder} 
              disabled={creatingPO}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {creatingPO ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Create PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Supplier Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Email Purchase Order
            </DialogTitle>
            <DialogDescription>
              Send PO request to {emailingPO?.supplier_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>To</Label>
              <Input
                type="email"
                value={emailData.to}
                onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                placeholder="supplier@email.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={emailData.message}
                onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                rows={8}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendEmailToSupplier} 
              disabled={sendingEmail}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Email & Create PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReorderSuggestionsTab;
